import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import { colorTokens } from "../../../styles/globalStyles";
import { parseMailPlaceholderKeyFromToken } from "./mailLineParts";

export const MAIL_ENVELOPE_CONTROL_HEIGHT_PX = 40;

export function mailPlaceholderPresentableLabel(
  key: string,
  definitions: readonly MailPlaceholderDefinition[],
): string {
  const d = definitions.find((x) => x.key === key);
  const t = d?.label?.trim();

  return t && t.length > 0 ? t : key;
}

function normalizePlaceholderCopy(s: string): string {
  return s.replace(/[.…\s]+$/u, "").trim();
}

export function mailPlaceholderTooltip(def: MailPlaceholderDefinition): string {
  const label = def.label?.trim() || def.key;
  const description = def.description?.trim();

  if (!description) {
    return label;
  }

  if (normalizePlaceholderCopy(description) === normalizePlaceholderCopy(label)) {
    return label;
  }

  return description;
}

export function insertSnippetInTextField(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  value: string,
  snippet: string,
  commit: (next: string) => void,
): boolean {
  if (!el || snippet.length === 0) {
    return false;
  }

  const focused = document.activeElement === el;
  const start = focused ? (el.selectionStart ?? value.length) : value.length;
  const end = focused ? (el.selectionEnd ?? start) : value.length;
  const next = value.slice(0, start) + snippet + value.slice(end);
  const caret = start + snippet.length;

  commit(next);

  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(caret, caret);
  });

  return true;
}

export function mailEnvelopeRecipientDisplayLabel(
  raw: string,
  definitions?: readonly MailPlaceholderDefinition[],
): string {
  const key = parseMailPlaceholderKeyFromToken(raw.trim());

  if (!key) {
    return raw;
  }

  if (definitions && definitions.length > 0) {
    return mailPlaceholderPresentableLabel(key, definitions);
  }

  return raw;
}

export const mailEditorToolbarRowSx = {
  px: { xs: 1, sm: 1.5 },
  py: 1,
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexShrink: 0,
  flexWrap: "wrap",
  borderBottom: `1px solid ${colorTokens.border.default}`,
} as const;

export const mailEditorBelowToolbarSx = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 1,
  p: { xs: 1, sm: 1.25 },
  overflow: "hidden",
} as const;
